import type { ReactNode } from 'react';
import type { RowAction } from '../ui/viz';

/* Small local pieces for the Marketplace screens (shops, products, categories, customers,
   catalogue rules). Built on the dashboard layer (styles/dash.css); styles live in catalog.css. */

export type DotTone = 'ok' | 'warn' | 'bad' | 'off' | 'info';

/** The ui/bits tones mapped to a status dot. */
export function dotTone(t: 'success' | 'warning' | 'error' | 'info' | 'neutral'): DotTone {
  return t === 'success' ? 'ok' : t === 'warning' ? 'warn' : t === 'error' ? 'bad' : t === 'info' ? 'info' : 'off';
}

/** A status dot with its word beside it (or only for screen readers when `quiet`). */
export function StDot({ tone, children, quiet }: { tone: DotTone; children: ReactNode; quiet?: boolean }) {
  return (
    <span className="mk-st">
      <i className={`mk-dot ${tone}`} aria-hidden="true" />
      {quiet ? <span className="sr-only">{children}</span> : <span>{children}</span>}
    </span>
  );
}

/** A slim horizontal meter (0..1), coloured by tone. */
export function Meter({ value, tone = 'ink', label }: { value: number; tone?: 'ink' | 'ok' | 'warn' | 'bad'; label?: string }) {
  const v = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
  return (
    <span className={`mk-meter ${tone}`} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <i style={{ width: `${Math.max(v > 0 ? 4 : 0, v * 100)}%` }} />
    </span>
  );
}

/**
 * The ui/viz RowMenu keeps its panel open after a pick; this closes any open one (its outside-click
 * listener) so the row reads clean again once the action runs.
 */
export function closeMenus() {
  if (typeof document === 'undefined') return;
  document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
}

/** Wrap RowMenu items so each pick closes the menu first. */
export function menuItems(items: (RowAction | null | false | undefined)[]): RowAction[] {
  return items.filter((x): x is RowAction => !!x).map((it) => ({
    ...it,
    onSelect: () => {
      closeMenus();
      it.onSelect();
    },
  }));
}
